// Bottom navigation — the MOBILE menu.
//
// Product decision (2026-07-23, Andre): on a phone the menu belongs under the thumb, not behind a
// top-left hamburger, which is the hardest target to reach one-handed. Below the mobile breakpoint
// this bar IS the navigation; from that breakpoint up the desktop side drawer takes over.
//
// The bar is ALWAYS in the DOM — `public/brand/aboo.css` hides it above the breakpoint with the
// same `max-width:767px` query the drawer already uses. No resize listener, no JS breakpoint that
// can drift out of sync with the CSS one, and nothing to re-render on rotate.
//
// Safe area: the bar pads itself with `env(safe-area-inset-bottom)` (index.html already sets
// `viewport-fit=cover`), and the same query adds matching bottom padding to `.content` so page
// content never hides behind it.
import { html, signal, navigate } from './t4.js';
import { icon } from './icons.js';

// items: [{ key, label, icon, path?, onClick?, confirm? }]
//   path     — hash route to navigate to (bare path; the router adds the '#')
//   onClick  — used instead of `path` for an action item
//   confirm  — question to show IN the bar before running onClick (destructive actions).
//              The bar is the easiest thing on the screen to hit by accident, so anything
//              irreversible on it is two taps, never one.
export function bottomNav(items, activeKey = '') {
  const confirming = signal('');   // key of the item awaiting a Yes/No, '' when idle

  function activate(item) {
    if (item.confirm) { confirming.value = item.key; return; }
    confirming.value = '';
    if (item.onClick) item.onClick(); else navigate(item.path);
  }

  const tab = item => html`
    <button type="button"
            class=${'bn-item' + (item.key === activeKey ? ' active' : '')}
            aria-current=${item.key === activeKey ? 'page' : 'false'}
            @click=${() => activate(item)}>
      <span class="bn-ico" aria-hidden="true">${icon(item.icon)}</span>
      <span class="bn-lbl">${item.label}</span>
    </button>`;

  const confirmRow = item => html`
    <span class="bn-confirm">
      <span class="bn-confirm-q">${item.confirm}</span>
      <button type="button" class="btn-confirm-yes"
              @click=${() => { confirming.value = ''; item.onClick(); }}>Yes</button>
      <button type="button" class="btn-confirm-no"
              @click=${() => { confirming.value = ''; }}>No</button>
    </span>`;

  return html`
    <nav class="bottom-nav" aria-label="Primary">
      ${() => {
        const pending = items.find(i => i.key === confirming.value);
        return pending ? confirmRow(pending) : items.map(tab);
      }}
    </nav>`;
}

// The tenant bar. Kept honest: a tenant has exactly ONE destination today (`/home`) plus the
// per-company chart + import when a company is in scope — those are the only tenant screens that
// exist, so those are the only tabs. "Sign out" joins them because tenants have no drawer to hold
// it and on mobile the app-bar copy is hidden (see aboo.css); it is confirm-gated, so two taps.
//
// Chart comes BEFORE Import deliberately: you look at what you have, then map onto it. Labels stay
// short — a tab ellipsises past ~9 characters at 375px (measured, see plan/mobile-friendliness.md).
export function tenantNavItems({ companyId = null, onSignOut = null } = {}) {
  const items = [{ key: 'home', label: 'Home', icon: '⌂', path: '/home' }];
  if (companyId) {
    items.push({ key: 'chart', label: 'Chart', icon: '▤',
                 path: '/companies/' + companyId + '/accounts' });
    items.push({ key: 'import', label: 'Import', icon: '⇪',
                 path: '/companies/' + companyId + '/import' });
  }
  if (onSignOut) {
    items.push({ key: 'signout', label: 'Sign out', icon: '⏻',
                 confirm: 'Sign out?', onClick: onSignOut });
  }
  return items;
}
